import React, { useEffect, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { LabeledInput } from "@/Components/Form";
import { DateSelectDay, FormError, Formheader } from "@/Components";
import { Info } from "@/Components/Info";
import { LabeledPlacesAutocomplete } from "@/Components/PlacesAutocomplete";
import { MapPin, Trash } from "react-feather";
import { Inertia } from "@inertiajs/inertia";
import { ROLE_ID } from "@/Enums/roles";
import { toast } from "react-toastify";
import LayoutIcon from "@/Components/LayoutIcon";
import { Teams as TeamsIcon } from "@/Icons";
import AddReporterToTeamSidebar from "@/Pages/Teams/Partials/AddReporterToTeamSidebar";

export default function CreateTeam({ errors, users }) {
  let [values, setValues] = useState({
    name: "",
    equity: 100,
    report_frequency: 0,
    start_date: new Date(),
    end_date: "",
    location: "",
    location_place_id: null,
    users: [],
  });

  let [isAddUserOpen, setIsAddUserOpen] = useState(false);

  function handleUpdate(event) {
    setValues((prevValues) => {
      return { ...prevValues, [event.target.name]: event.target.value };
    });
  }

  function setDate(date, property) {
    setValues((prevValues) => ({
      ...prevValues,
      [property]: date,
    }));
  }

  function setLocation({ description, place_id }) {
    setValues((prevValues) => ({
      ...prevValues,
      location: description,
      location_place_id: place_id,
    }));
  }

  function handleDeleteUser(userId) {
    setValues((prevValues) => ({
      ...prevValues,
      users: prevValues.users.filter((u) => u !== userId),
    }));
  }

  function handleSave(e) {
    e.preventDefault();

    let data = {
      name: values.name,
      equity: values.equity,
      report_frequency: values.report_frequency,
      location: values.location,
      location_place_id: values.location_place_id,
      start_date: values.start_date,
      users: values.users || [],
    };

    // End date is optional
    if (values.end_date) {
      data.end_date = values.end_date;
    }

    const id = toast.loading("Please wait...");

    Inertia.post(`/teams`, data, {
      onSuccess: () => {
        toast.update(id, {
          render: "The team was successfully created!",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        Inertia.visit("/teams");
      },
      onError: () => {
        toast.update(id, {
          render: "There was an error creating the team",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  }

  return (
    <>
      <form onSubmit={handleSave} className="mb-8 flex flex-col gap-4">
        <div className="shadow-small flex flex-wrap bg-gray-light px-8 py-4">
          <div className="w-full">
            <Formheader>Team details</Formheader>
          </div>

          <div className="mb-4 w-full md:w-1/2 md:pr-3">
            <LabeledInput
              title="Team name"
              name="name"
              type="text"
              placeholder="Enter team name"
              value={values.name}
              onChange={handleUpdate}
            />
            <FormError error={errors.name} />
          </div>

          <div className="mb-4 w-full md:w-1/2">
            <LabeledInput
              unit="%"
              title={
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <span>Consolidation approach</span>
                    <Info>
                      The consolidation approach for emissions from teams can be
                      based on either equity share, financial- or operational
                      control. This percentage reflects this teams level of
                      control and, consequently, the extent to which the teams
                      emissions are consolidated into your total emissions
                      footprint. Specify with the percentage ranging from 0 to
                      100%.
                    </Info>
                  </div>
                </div>
              }
              placeholder="Enter consolidation approach amount"
              name="equity"
              required="required"
              type="number"
              min="0"
              max="100"
              value={values.equity}
              onChange={handleUpdate}
            />
            <FormError error={errors.equity} />
          </div>

          <div className="mb-4 w-full md:w-1/2 md:pr-3">
            <label>
              <span className="mb-1 block text-[14px]">Start date</span>
            </label>
            <DateSelectDay
              onChange={(date) => setDate(date, "start_date")}
              value={values.start_date}
            />
            <p className="mt-1 text-sm text-gray-600">
              This date decide the beginning of your emission accounting for the
              team. Start now, in the future or in the past.
            </p>
            <FormError error={errors.start_date} />
          </div>

          <div className="mb-4 w-full md:w-1/2">
            <label>
              <span className="mb-1 block text-[14px]">End date</span>
            </label>
            <DateSelectDay
              onChange={(date) => setDate(date, "end_date")}
              value={values.end_date}
            />
            <p className="mt-1 text-sm text-gray-600">
              Set an end date if you want to automatically stop the reporting
              and notifications
            </p>
            <FormError error={errors.end_date} />
          </div>

          <div className="relative mb-4 w-full">
            <LabeledPlacesAutocomplete
              title="Geographic location"
              placeholder="Enter name of town or region"
              className="w-full pl-10"
              name="location"
              defaultValue={values.location}
              onChange={setLocation}
            />
            <MapPin size={18} className="absolute left-3 -mt-8" />
            <FormError error={errors.location} />
          </div>
        </div>

        <div className="shadow-small flex flex-wrap bg-gray-light px-8 py-4">
          <div className="w-full">
            <Formheader>Reporters</Formheader>
          </div>

          {values.users &&
            values.users.map((user_id) => {
              const user = users.data.find((u) => u.id === user_id);

              return (
                <div
                  key={user_id}
                  className="mb-2 flex w-full justify-between border border-gray-lighter bg-gray-light px-4 py-2 shadow-sm"
                >
                  <div>
                    {user.organization_role_id === ROLE_ID.OWNER && (
                      <img
                        className="-mt-1 mr-2 inline w-[20px]"
                        src={`/images/diamond.svg`}
                        alt=""
                      />
                    )}
                    {user.firstname && user.surname ? (
                      <span className="font-bold">{user.fullname}</span>
                    ) : (
                      user.email
                    )}
                  </div>

                  <span className="hover-circle relative">
                    <Trash
                      className="cursor-pointer"
                      onClick={() => handleDeleteUser(user.id)}
                    />
                  </span>
                </div>
              );
            })}

          <div className="mb-4 w-full">
            {values?.users?.length === 0 && (
              <p className="text-sm text-gray-600">
                No reporters have been added to this team
                <br />
                Click "Add reporters" to select your reporter(s)
              </p>
            )}

            <button
              type="button"
              className="btn-blue mt-2"
              onClick={() => {
                setIsAddUserOpen(!isAddUserOpen);
              }}
            >
              Add reporters
            </button>
          </div>

          <div className="mb-4 w-full">
            <p>How often should reporters get reminders to report?</p>
            <div className="mt-2 flex flex-col gap-2 md:flex-row md:gap-4">
              <label>
                <input
                  checked={Number(values.report_frequency) === 0}
                  value={0}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">No reports</span>
              </label>

              <label>
                <input
                  checked={Number(values.report_frequency) === 1}
                  value={1}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Monthly</span>
              </label>

              <label>
                <input
                  checked={Number(values.report_frequency) === 3}
                  value={3}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Quarterly</span>
              </label>
              <label>
                <input
                  checked={Number(values.report_frequency) === 12}
                  value={12}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Yearly</span>
              </label>
            </div>
            <FormError error={errors.report_frequency} />
          </div>
        </div>

        <div className="flex w-full flex-row justify-end">
          <div className="flex-shrink-0">
            <button className="btn-blue ml-4">Create team</button>
          </div>
        </div>
      </form>

      <AddReporterToTeamSidebar
        openModal={isAddUserOpen}
        setOpenModal={setIsAddUserOpen}
        values={values}
        setValues={setValues}
      />
    </>
  );
}

CreateTeam.layout = (page) => (
  <AppLayout title="Create new team">
    <OrganizationLayout
      header="New team"
      backAction={{ label: "Back", callback: () => Inertia.visit("/teams") }}
      icon={
        <LayoutIcon>
          <TeamsIcon size={40} className="text-black" />
        </LayoutIcon>
      }
    >
      {page}
    </OrganizationLayout>
  </AppLayout>
);
